<template>
	<main class="cc-alerts" data-tour="cc-alerts-overview">
		<h1 class="page-title">Alerts</h1>
		<section class="alert-tiles-list" data-tour="cc-alert-tiles">
			<AlertTile
				v-for="(tile, index) in alertTiles"
				:key="index"
				:alertType="tile.alertType"
				:alertTitle="tile.alertTitle"
				:alertSubtitle="tile.alertSubtitle"
				:alertInfo="tile.alertInfo"
				:active="tile.active"
				@alertTileClicked="handleAlertFilter"
			/>
		</section>
		<div class="alert-actions-container">
			<SearchInput
				ariaLabel="'Search for alerts'"
				placeholder="Search alerts..."
				@onSearchInputChange="handleSearchInputChange"
				data-tour="cc-alert-table-search"
			/>
			<OwnerMenu
				:options="selectedContactsIds"
				v-if="canPerformProfileSelector"
				@onFilterChange="handleFilterChange"
				:brokerContactsList="sortedBrokerShopUsers"
			/>
			<FilterMenu
				data-tour="cc-alert-filter"
				:filterOptions="filterOptions"
				:selectedFilters="filters"
				@onFilterChange="handleFilterChange"
				@onResetFilter="handleResetFilter"
			/>
		</div>
		<FallBack v-if="isLoading && !error">
			<LoadingIcon />
		</FallBack>
		<FallBack
			v-if="!error && !isLoading && !noAlertsFound && noResultsFound"
			iconStyle="fal"
			icon="exclamation-triangle"
			message="Sorry, there are no results."
		/>
		<FallBack
			v-if="!error && !isLoading && noAlertsFound"
			iconStyle="far"
			icon="bell"
			message="Sorry, you have no alerts."
		/>
		<Table
			v-if="!isLoading && !error && !isEmpty(alerts)"
			class="table"
			aria-label="Alerts list"
		>
			<thead class="table-head">
				<tr
					class="table-head-row"
					:class="{ 'show-owner': canPerformProfileSelector }"
				>
					<th scope="col" class="table-head__checkbox">
						<CheckBox
							:checked="isCurrentPageSelected()"
							@onClick="handleSelectAll"
							:aria-label="`Select all alerts on the current page`"
						/>
					</th>
					<th
						v-for="(columnHeader, index) in columnHeaders"
						:key="index"
						@click="handleSort(columnHeader.value)"
						scope="col"
						:class="`table-head__${columnHeader.className}`"
					>
						{{ columnHeader.name }}
						<font-awesome-icon
							v-if="
								sortDirection === 'asc' &&
								sortColumn === columnHeader.value
							"
							:icon="['fad', 'sort-down']"
							class="sort-down-icon"
						/>
						<font-awesome-icon
							v-else-if="
								sortDirection === 'desc' &&
								sortColumn === columnHeader.value
							"
							:icon="['fad', 'sort-up']"
							class="sort-up-icon"
						/>
						<font-awesome-icon v-else :icon="['fas', 'sort']" />
					</th>
					<th scope="col" class="table-head__quick-actions">
						<span class="quick-actions">
							<QuickActionsMenu
								:lightButton="true"
								:lightButtonIcon="['far', 'ellipsis-h']"
								:menus="populateMultipleQuickActions()"
								:isMultiple="true"
								:selectedContacts="
									Object.keys(selectedAlerts).length
								"
								:showCounter="true"
								@markAsToDo="
									handleUpdateAlertStatus(
										STATUS_ACTION_TYPES.TO_DO,
									)
								"
								@markAsDone="
									handleUpdateAlertStatus(
										STATUS_ACTION_TYPES.DONE,
									)
								"
								@markAsDismissed="
									handleUpdateAlertStatus(
										STATUS_ACTION_TYPES.DISMISSED,
									)
								"
							/>
						</span>
					</th>
				</tr>
			</thead>
			<tbody class="table-body" v-if="currentPageAlerts">
				<tr
					:class="{ 'show-owner': canPerformProfileSelector }"
					class="table-body-row"
					v-for="(alert, index) in currentPageAlerts"
					:key="index"
				>
					<td class="table-body__checkbox">
						<CheckBox
							:checked="!!selectedAlerts[alert.alertId]"
							@onClick="handleAlertSelection(alert, ...arguments)"
							:aria-label="`Select the alert for ${alert.lead.firstName} ${alert.lead.lastName}`"
						/>
					</td>
					<td class="table-body__type">
						<div class="alert-icon">
							<AlertTypeIcon :alertType="alert.type" />
						</div>
						<div class="alert-type">
							<span
								role="button"
								aria-label="Alert type"
								class="type"
								@click="
									handleAlertClick(
										alert.lead,
										alert.alertId,
										alert.status,
									)
								"
							>
								{{ alert.type }}
							</span>
							<span>
								{{
									alert.type === 'Birthday'
										? getDateString(
												alert.alertDateTime,
												alert.type,
										  )
										: alert.value
								}}</span
							>
						</div>
					</td>
					<td class="table-body__contact">
						<ContactIcon
							:firstName="alert.lead.firstName"
							:lastName="alert.lead.lastName"
						/>
						<div class="alert-type-info">
							<span>
								<TextButton
									aria-label="Contact Full Name"
									@onClick="handleContactClick(alert.lead)"
									:title="
										truncateModalFields({
											fieldName: `${alert.lead.firstName} ${alert.lead.lastName}`,
											fieldLength: 20,
											attributeName: 'title',
										})
									"
								>
									{{
										truncateModalFieldByWord({
											individualWordLength: 12,
											fieldValue: `${alert.lead.firstName} ${alert.lead.lastName}`,
										})
									}}
								</TextButton>
							</span>
							<span>{{ alert.lead.type }}</span>
						</div>
					</td>
					<td class="table-body__date">
						{{ getDateString(alert.alertDateTime) }}
					</td>
					<td class="table-body__status">
						<AlertStatus :status="alert.status" />
					</td>
					<td
						v-if="canPerformProfileSelector"
						class="table-body__owner"
					>
						{{ alert.lead.owner }}
					</td>
					<td class="table-body__quick-actions">
						<QuickActionsMenu
							:lightButton="true"
							:lightButtonIcon="['far', 'ellipsis-h']"
							:status="alert.status"
							:menus="populateSingleQuickActions(alert)"
							@viewContact="handleContactClick(alert.lead)"
							@viewAlert="
								handleAlertClick(
									alert.lead,
									alert.alertId,
									alert.status,
								)
							"
							@sendEmail="handleSendEmail(alert)"
							@addNote="
								handleAddNoteClick(alert.lead, alert.alertId)
							"
							@sendToBlink="handleBlinkClick(alert.lead)"
							@viewInEase="openInEase(alert.loan.easeLoanUrl)"
							@markAsToDo="
								handleUpdateAlertStatus(
									STATUS_ACTION_TYPES.TO_DO,
									alert.alertId,
									'single',
								)
							"
							@markAsDone="
								handleUpdateAlertStatus(
									STATUS_ACTION_TYPES.DONE,
									alert.alertId,
									'single',
								)
							"
							@markAsDismissed="
								handleUpdateAlertStatus(
									STATUS_ACTION_TYPES.DISMISSED,
									alert.alertId,
									'single',
								)
							"
						/>
					</td>
				</tr>
			</tbody>
		</Table>
		<Pagination
			v-if="totalCount > 20 && !isLoading && !error"
			@onPageChange="handlePageNumberChange"
			@onPageSizeChange="handlePageSizeChange"
			:totalCount="totalCount"
			:pageNumber="pageNumber"
			:pageSize="pageSize"
		/>
		<OverviewModal
			v-if="showOverviewModal"
			@close="handleOverviewModalClose"
			:leadId="leadIdClicked"
			:alertId="alertIdClicked"
			:modalView="modalView"
			:success="success"
			:waiting="waiting"
			:typeOfNote="'alert'"
			:leadInfo="leadInfo"
			:sendContact="sendContactToBlink"
			:blinkDisabled="blinkDisabled"
			:blinkUnregistered="blinkUnregistered"
			:loggedInUserId="loggedInUserId"
			@alertStatusUpdated="getAlerts(true)"
			@contactTransferred="updateAlertList"
			@contactDeleted="updateAlertList"
		/>
	</main>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { debounce, isEmpty } from 'lodash';

import AlertStatus from './AlertStatus.vue';
import CheckBox from './CheckBox.vue';
import OverviewModal from './modals/OverviewModal.vue';
import FallBack from '../../components/global/FallBack.vue';
import FilterMenu from '@/components/global/FilterMenu.vue';
import OwnerMenu from './OwnerMenu.vue';
import LoadingIcon from '../../components/global/LoadingIcon.vue';
import Pagination from '../../components/global/Pagination.vue';
import SearchInput from '../../components/global/SearchInput.vue';
import TextButton from '../../components/global/TextButton.vue';
import Table from '../../components/global/Table.vue';
import QuickActionsMenu from './QuickActionsMenu.vue';
import AlertTypeIcon from './AlertTypeIcon.vue';
import AlertTile from './AlertTile.vue';
import ContactIcon from './ContactIcon.vue';
import {
	scrollToTop,
	disableAllQuickActions,
	getAlertQuickActions,
	openInEase,
	truncateModalFields,
	truncateModalFieldByWord,
} from './helpers';
import {
	ALERTS,
	CONTACTS,
	STATUS_ACTION_TYPES,
	DEFAULT_STATUS_FILTERS,
	TYPES,
	DATE_RANGE_TYPES,
} from './constants';
import { VIEWS as MODAL_VIEWS } from './modals/constants';
import { CAMPAIGN_ID } from '../../constants/brand-builder';
import alertsService from '@/services/contacts/alerts-service.js';

export default {
	name: 'ClientConnectV2Alerts',
	data() {
		return {
			error: null,
			isLoading: null,
			searchTerm: '',
			sortColumn: '',
			sortDirection: 'asc',
			pageNumber: 1,
			pageSize: 20,
			currentPageAlerts: [],
			slicedAlerts: [],
			showOverviewModal: false,
			leadIdClicked: null,
			alertIdClicked: null,
			selectedAlerts: {},
			selectedAlertsStatusChangable: true,
			MODAL_VIEWS,
			success: false,
			waiting: false,
			modalView: null,
			CONTACTS,
			STATUS_ACTION_TYPES,
			DATE_RANGE_TYPES,

			alertTiles: [
				{
					name: 'todaysNewAlertCount',
					alertType: 'New Alerts',
					alertTitle: "Today's New Alerts",
					alertInfo: null,
					active: false,
				},
				{
					name: 'toDoAlertCount',
					alertType: 'Alert',
					alertTitle: 'New & To-Do Alerts',
					alertInfo: null,
					active: false,
				},
				{
					name: 'doneForYouAlertCount',
					alertType: 'Email',
					alertTitle: 'Done for You',
					alertSubtitle: '(30 Days)',
					alertInfo: null,
					active: false,
				},
			],
			TYPES,
			leadInfo: {
				firstName: null,
				lastName: null,
				email: null,
			},
			sendContactToBlink: false,
			blinkDisabled: false,
			blinkUnregistered: false,
			alertTileClicked: null,
			filters: {
				[TYPES.STATUS]: DEFAULT_STATUS_FILTERS,
				[TYPES.DATE_RANGE]: [DATE_RANGE_TYPES.PREVIOUS_30_DAYS],
			},
		};
	},
	components: {
		AlertStatus,
		CheckBox,
		OverviewModal,
		FallBack,
		FilterMenu,
		OwnerMenu,
		LoadingIcon,
		Pagination,
		SearchInput,
		TextButton,
		Table,
		QuickActionsMenu,
		AlertTypeIcon,
		AlertTile,
		ContactIcon,
	},
	computed: {
		...mapGetters([
			'currentUserRolodexIds',
			'sortedBrokerShopUsers',
			'isImpersonated',
		]),
		...mapState({
			alerts: (state) => state.clientConnectV2.alerts.items,
			noAlertsFound: (state) =>
				state.clientConnectV2.alerts.noAlertsFound,
			noResultsFound: (state) =>
				state.clientConnectV2.alerts.noResultsFound,
			currentFilters: (state) =>
				state.clientConnectV2.alertsView.currentFilters,
			onInit: (state) => state.clientConnectV2.alerts.onInit,
			totalCount: (state) => state.clientConnectV2.alerts.totalCount,
			types: (state) => state.clientConnectV2.alerts.types,
			statuses: (state) => state.clientConnectV2.alerts.statuses,
			dateRanges: (state) => state.clientConnectV2.alerts.dateRanges,
			statusTotals: (state) =>
				state.clientConnectV2.alerts.alertStatusTotals,
			companyHasBlinkRegistration: (state) =>
				state.companyProfile.blinkRegistrationInd,
			userHasBlinkRegistration: (state) =>
				state.profile.blinkRegistrationInd,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			loggedInUserId: (state) =>
				state.profileSwitcher.mainProfile.rolodexContactId,
			canPerformProfileSelector: (state) =>
				state.profileSwitcher.mainProfileSelector
					.canPerformProfileSelector,
			columnHeaders: (state) => {
				let headers = ALERTS.TABLE_HEADERS;
				if (
					!state.profileSwitcher.mainProfileSelector
						.canPerformProfileSelector
				) {
					headers = headers.filter((h) => h.name !== 'Owner');
				}
				return headers;
			},
			typesIds: (state) => state.clientConnectV2.alertsView.typesIds,
			selectedContactsIds: (state) =>
				state.clientConnectV2.selectedContactsIds,
			selectedContactIdsUpdated: (state) =>
				state.clientConnectV2.selectedContactIdsUpdated,
			statusesIds: (state) =>
				state.clientConnectV2.alertsView.statusesIds,
			dateRange: (state) => state.clientConnectV2.alertsView.dateRange,
		}),
		filterOptions() {
			return [this.types, this.statuses, this.dateRanges];
		},
		isBirthday() {
			if (alertType === 'Birthday') {
				return true;
			} else return false;
		},
	},
	methods: {
		...mapActions([
			'clearClientConnectAlerts',
			'getClientConnectAlerts',
			'getAlertStatusTotals',
			'storeCurrentAlertsViewFilters',
			'updateSelectedContactsIds',
			'updateAlertsViewTypesIds',
			'updateAlertsViewStatusesIds',
			'updateAlertsViewDateRange',
		]),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see note above
		isEmpty,
		openInEase,
		truncateModalFields,
		truncateModalFieldByWord,
		// Destructures the object passed in to only take the properties needed
		populateSingleQuickActions({ contactId, statusId, loan, lead, type }) {
			this.blinkDisabled = this.isImpersonated;
			let easeUrl = '';
			let isUwmLoan = false;
			let isLoanAlert = false;

			if (loan) {
				isLoanAlert = true;
				easeUrl = loan.easeLoanUrl;
				isUwmLoan = lead.uwmInd;
			}
			return getAlertQuickActions(
				true,
				contactId,
				statusId,
				this.mainProfileIsInternalUser,
				this.isImpersonated,
				this.loggedInUserId,
				isLoanAlert,
				easeUrl,
				isUwmLoan,
				type,
			);
		},
		populateMultipleQuickActions() {
			return [
				[
					{
						icon: ['far', 'circle'],
						text: 'Mark as To-Do',
						action: 'markAsToDo',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.selectedAlertsStatusChangable,
					},
					{
						icon: ['far', 'check-circle'],
						text: 'Mark as Done',
						action: 'markAsDone',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.selectedAlertsStatusChangable,
					},
					{
						icon: ['far', 'times-circle'],
						text: 'Mark as Dismissed',
						action: 'markAsDismissed',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.selectedAlertsStatusChangable,
					},
				],
			];
		},
		async getAlerts(bySearchTerm, statusesIds) {
			try {
				const { entityId, contactsIds } = this.currentUserRolodexIds;
				const payload = {
					data: {
						entityId,
						contactsIds: isEmpty(this.selectedContactsIds)
							? contactsIds
							: this.selectedContactsIds,
						typesIds: this.typesIds,
						statusesIds: statusesIds
							? statusesIds
							: this.statusesIds,
					},
					searchTerm: this.searchTerm,
					sortColumn: this.sortColumn,
					sortDirection: this.sortDirection,
					pageNumber: this.pageNumber,
					pageSize: this.pageSize,
					dateRange: this.dateRange,
				};
				await this.getClientConnectAlerts({ payload, bySearchTerm });
			} catch (error) {
				this.error = error;
			}
		},
		async handleSort(column) {
			if (this.sortColumn !== column) {
				this.sortColumn = column;
				this.sortDirection = 'asc';
			} else {
				this.sortDirection =
					this.sortDirection === 'asc' ? 'desc' : 'asc';
			}
			this.pageNumber = 1;
			this.isLoading = true;
			await this.getAlerts(true);
			this.isLoading = false;
		},
		async handleFilterChange({ filters, selectedContactsIds, clickCheck }) {
			if (filters) {
				this.updateAlertsViewTypesIds(filters[TYPES.ALERT]);
				this.updateAlertsViewStatusesIds(filters[TYPES.STATUS]);
				this.updateAlertsViewDateRange(filters[TYPES.DATE_RANGE][0]);
			}
			selectedContactsIds &&
				this.updateSelectedContactsIds(selectedContactsIds);
			const currentAlertFilters = {
				typesIds: this.typesIds || [],
				statusesIds: this.statusesIds || [],
				dateRange: this.dateRange,
				selectedContactsIds: this.selectedContactsIds || [],
			};
			await this.storeCurrentAlertsViewFilters(currentAlertFilters);
			this.setCurrentFilters();

			this.pageNumber = 1;
			this.isLoading = true;

			if (clickCheck === 'ownerFilterClicked') {
				await this.getAlertStatusCount();
			}
			if (clickCheck !== 'alertTilesClicked') {
				this.unselectAllAlertTiles();
			}
			await this.getAlerts(true);
			this.getCurrentPageAlerts();
			this.isLoading = false;
		},
		handleResetFilter() {
			this.filters = {
				[TYPES.STATUS]: DEFAULT_STATUS_FILTERS,
				[TYPES.DATE_RANGE]: [DATE_RANGE_TYPES.PREVIOUS_30_DAYS],
			};
		},
		unselectAllAlertTiles() {
			this.alertTiles.forEach((tile) => {
				tile.active = false;
			});
		},
		handleAlertFilter(alertName) {
			let statusesIds = [];
			let dateRange = null;
			let clickCheck = 'alertTilesClicked';

			this.alertTiles.forEach((tile) => {
				if (tile.alertType === alertName) {
					tile.active = !tile.active;

					if (tile.active === false) {
						statusesIds = DEFAULT_STATUS_FILTERS;
						dateRange = DATE_RANGE_TYPES.PREVIOUS_30_DAYS;
					} else {
						switch (tile.alertType) {
							case 'New Alerts':
								statusesIds = [STATUS_ACTION_TYPES.NEW];
								dateRange = DATE_RANGE_TYPES.TODAY;
								break;
							case 'Alert':
								statusesIds = [
									STATUS_ACTION_TYPES.NEW,
									STATUS_ACTION_TYPES.TO_DO,
								];
								dateRange = DATE_RANGE_TYPES.PREVIOUS_30_DAYS;
								break;
							case 'Email':
								statusesIds = [
									STATUS_ACTION_TYPES.DONE_FOR_YOU,
								];
								dateRange = DATE_RANGE_TYPES.PREVIOUS_30_DAYS;
								break;
							default:
								break;
						}
					}
					this.filters[TYPES.STATUS] = statusesIds;
					this.filters[TYPES.DATE_RANGE] = [dateRange];
				} else {
					tile.active = false;
				}
			});

			this.handleFilterChange({
				filters: this.filters,
				selectedContactsIds: this.selectedContactsIds,
				clickCheck,
			});
		},
		handleOverviewModalClose() {
			this.showOverviewModal = false;
			this.leadIdClicked = null;
			this.alertIdClicked = null;
			this.success = false;
		},
		handleAddBlinkData(lead) {
			this.leadInfo.firstName = lead.firstName;
			this.leadInfo.lastName = lead.lastName;
			this.leadInfo.email = lead.email;
			this.leadInfo.birthday = lead.birthday;
			if (
				this.companyHasBlinkRegistration &&
				this.userHasBlinkRegistration
			) {
				this.blinkUnregistered = false;
			} else {
				this.blinkUnregistered = true;
			}
			this.sendContactToBlink = true;
		},
		handleBlinkClick(lead) {
			this.handleAddBlinkData(lead);
			this.leadIdClicked = lead.leadId;
			this.showOverviewModal = true;
			this.modalView = MODAL_VIEWS.SEND_TO_BLINK;
		},
		async handleAlertClick(lead, alertId, alertStatus) {
			this.handleAddBlinkData(lead);
			this.leadIdClicked = lead.leadId;
			this.alertIdClicked = alertId;
			this.showOverviewModal = true;
			this.modalView = MODAL_VIEWS.ALERT_DETAILS;

			// This changes the status from new to to-do when the user opens the alert
			if (alertStatus === 'New' && !this.mainProfileIsInternalUser) {
				const payload = {
					alertIds: [alertId],
					statusId: STATUS_ACTION_TYPES.TO_DO,
				};
				await alertsService.updateAlertStatus(payload);
				await this.getAlerts(true);
			}
		},
		handleAddNoteClick(lead, alertId) {
			this.leadIdClicked = lead.leadId;
			this.alertIdClicked = alertId;
			this.handleAddBlinkData(lead);
			this.showOverviewModal = true;
			this.modalView = MODAL_VIEWS.ADD_NOTE;
		},
		//add optional param for bday to send back short date
		getDateString(date, alertType = '') {
			if (alertType === 'Birthday') {
				return new Date(date).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
				});
			} else {
				return new Date(date).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				});
			}
		},
		handlePageNumberChange(page) {
			this.pageNumber = page;
			scrollToTop();
			this.getCurrentPageAlerts();
		},
		handlePageSizeChange(size) {
			if (this.pageSize !== size) {
				this.pageSize = size;
				this.pageNumber = 1;
				this.getCurrentPageAlerts();
			}
		},
		handleSearchInputChange(value) {
			this.searchTerm = value;
		},
		handleContactClick(lead) {
			this.leadIdClicked = lead.leadId;
			this.handleAddBlinkData(lead);
			this.showOverviewModal = true;
			this.modalView = MODAL_VIEWS.HOME;
		},
		// if alert is selected adds alertId to the selectedAlerts object else removes it
		handleAlertSelection(alert, selected) {
			if (selected) {
				this.selectedAlerts[alert.alertId] = {
					alertId: alert.alertId,
					statusId: alert.statusId,
				};
			} else {
				delete this.selectedAlerts[alert.alertId];
			}
			// this is only to let Vue's reactivity system know the selected alerts updated
			this.selectedAlerts = { ...this.selectedAlerts };
		},
		// returns true only if all of the current page alerts are selected else false
		isCurrentPageSelected() {
			return (
				!isEmpty(this.currentPageAlerts) &&
				this.currentPageAlerts.every(
					(alert) => this.selectedAlerts[alert.alertId],
				)
			);
		},
		// if selected adds currentPage alertIds to selectedAlerts object, else removes them
		handleSelectAll(selected) {
			if (selected) {
				this.currentPageAlerts.forEach(
					(alert) =>
						(this.selectedAlerts[alert.alertId] = {
							alertId: alert.alertId,
							statusId: alert.statusId,
						}),
				);
			} else {
				this.currentPageAlerts.forEach(
					(alert) => delete this.selectedAlerts[alert.alertId],
				);
			}
			this.selectedAlerts = { ...this.selectedAlerts };
		},
		getCurrentPageAlerts() {
			this.slicedAlerts = this.alerts.slice(
				(this.pageNumber - 1) * this.pageSize,
				this.pageNumber * this.pageSize,
			);
			if (!isEmpty(this.slicedAlerts)) {
				this.currentPageAlerts = [...this.slicedAlerts];
			}
		},
		async handleUpdateAlertStatus(statusId, alertId, alertsSelected) {
			let payload = {};

			const alertIdToChangeStatus =
				alertsSelected === 'single' ? alertId : null;

			if (alertIdToChangeStatus !== null) {
				payload = {
					alertIds: [alertIdToChangeStatus],
					statusId,
				};
				// remove this alert from the list of selected alerts
				delete this.selectedAlerts[alertIdToChangeStatus];
			} else {
				// multiple alerts changing
				// remove any that can't have status changed
				const statusChangableAlerts = Object.values(
					this.selectedAlerts,
				).filter(
					(alert) =>
						alert.statusId !== STATUS_ACTION_TYPES.DONE_FOR_YOU &&
						alert.statusId !== STATUS_ACTION_TYPES.SCHEDULED,
				);
				payload = {
					alertIds: statusChangableAlerts.map(
						(alert) => alert.alertId,
					),
					statusId,
				};
				this.selectedAlerts = {};
			}
			if (payload.alertIds.length > 0) {
				this.isLoading = true;
				await alertsService.updateAlertStatus(payload);
				await this.getAlerts(true);
				this.isLoading = false;
			}
		},
		async getAlertStatusCount() {
			const { entityId, contactsIds } = this.currentUserRolodexIds;
			const payload = {
				entityId,
				contactsIds: isEmpty(this.selectedContactsIds)
					? contactsIds
					: this.selectedContactsIds,
			};
			await this.getAlertStatusTotals(payload);

			this.alertTiles.forEach((alert) => {
				alert.alertInfo = this.statusTotals[alert.name];
			});
		},
		async updateAlertList() {
			this.showOverviewModal = false;
			this.pageNumber = 1;
			this.isLoading = true;
			try {
				await this.getAlerts(true);
			} catch (error) {
				this.error = error;
			}
			this.isLoading = false;
		},
		handleSendEmail(alert) {
			const campaignId = CAMPAIGN_ID[alert.type];
			this.$router.push({
				path: `/campaign-editor-workspace/${campaignId}/media`,
				query: {
					alertId: alert.alertId,
					borrowerFirstName: alert.lead.firstName,
					borrowerLastName: alert.lead.lastName,
					email: alert.lead.email,
					leadId: alert.lead.leadId,
					alertType: alert.type,
				},
			});
		},
		setCurrentFilters() {
			if (this.currentFilters) {
				// Use the filters stored in state
				this.updateAlertsViewTypesIds(
					this.currentFilters.typesIds || [],
				);
				this.updateAlertsViewStatusesIds(
					this.currentFilters.statusesIds || [],
				);
				this.updateAlertsViewDateRange(this.currentFilters.dateRange);
			} else {
				// default filters
				this.updateAlertsViewTypesIds([]);
				this.updateAlertsViewStatusesIds(DEFAULT_STATUS_FILTERS);
				this.updateAlertsViewDateRange(
					DATE_RANGE_TYPES.PREVIOUS_30_DAYS,
				);
			}
			if (this.selectedContactsIds) {
				this.updateSelectedContactsIds(this.selectedContactsIds || []);
			} else {
				this.updateSelectedContactsIds([]);
			}
			// update the FilterMenu
			this.filters = {
				[TYPES.ALERT]: this.typesIds,
				[TYPES.STATUS]: this.statusesIds,
				[TYPES.DATE_RANGE]: [this.dateRange],
			};
		},
		handleAlertsChange() {
			if (!this.currentFilters) {
				/* If there are no currentFilters stored on state but alerts have changed,
				then state may have reset while on the page.
				setCurrentFilters will update the current filters to the defaults. */
				this.setCurrentFilters();
			}
			this.getCurrentPageAlerts();
		},
	},
	async created() {
		this.isLoading = true;
		this.getAlertStatusCount();

		/* TODO: fix this if statement.
				multipleQuickActions does not exist, the following if statement does not do anything */
		if (this.mainProfileIsInternalUser) {
			disableAllQuickActions(this.multipleQuickActions);
		}
		this.setCurrentFilters();

		if (!isEmpty(this.$route.params)) {
			this.handleAlertFilter(this.$route.params.type);
		}

		this.isLoading = false;
	},
	mounted() {
		if (this.selectedContactIdsUpdated || this.onInit === true) {
			this.clearClientConnectAlerts();
			this.getAlerts();
		}
	},
	beforeDestroy() {
		if (this.searchTerm) {
			this.clearClientConnectAlerts();
		}
	},
	watch: {
		async slicedAlerts(value) {
			if (this.error) return;
			// Prevents alerts from loading twice when there was a search
			if (!this.alerts.length) return;
			const lastPage = Math.ceil(this.totalCount / this.pageSize);
			if (this.pageNumber === lastPage && !isEmpty(value)) {
				return;
			} else if (
				(isEmpty(value) || value.length !== this.pageSize) &&
				this.totalCount > this.alerts.length &&
				!this.error
			) {
				this.isLoading = true;
				await this.getAlerts();
				this.isLoading = false;
			}
		},
		searchTerm: debounce(async function () {
			this.pageNumber = 1;
			this.isLoading = true;
			await this.getAlerts(true);
			this.isLoading = false;
		}, 500),
		/* The immediate: true below is important when returning from the Contacts or Drip Campaigns tabs.
		We don't do a new search for alerts because we already have them.  So we need to do
		getCurrentPageAlerts() to repopulate the results. */
		alerts: {
			immediate: true,
			handler: 'handleAlertsChange',
		},
		selectedAlerts(currentSelectedAlerts) {
			// Determine if any of the selected alerts can have their status changed
			this.selectedAlertsStatusChangable = !!Object.values(
				currentSelectedAlerts,
			).filter(
				(alert) =>
					alert.statusId !== STATUS_ACTION_TYPES.DONE_FOR_YOU &&
					alert.statusId !== STATUS_ACTION_TYPES.SCHEDULED,
			).length;
		},
		async onInit(value) {
			// When ccv2.0 state is reset when the user is on this page, repopulate the data
			// (this will happens when profile is switched/impersonation)
			if (value) {
				this.isLoading = true;
				await this.getAlertStatusCount();
				await this.getAlerts();
				this.isLoading = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.alert-actions-container {
	display: flex;
	@include rem('gap', 15px);
	@include rem('margin-bottom', 15px);

	/deep/ .btn-quick-actions {
		@include rem('width', 44px);
		@include rem('height', 44px);
	}
}

.table-head__quick-actions {
	/deep/ .btn-quick-actions {
		@include rem('width', 44px);
		@include rem('height', 44px);
	}
}

.alert-actions-container .search-container {
	flex-grow: 1;
}

.alert-tiles-list {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	@include rem('grid-gap', 20px);
	@include rem('margin-bottom', 20px);

	@media only screen and (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
}

.table {
	background: $white;

	.table-head-row,
	.table-body-row {
		@include rem('padding', 2.5px 20px 2.5px 20px);
		display: grid;
		grid-template-columns: 5% 2fr 3fr 2fr 1fr 5%;
		gap: 15px;

		&.show-owner {
			@media only screen and (min-width: 1201px) {
				grid-template-columns: 5% 4fr 8fr 4fr 2fr 5fr 5%;
			}
		}
	}

	@media only screen and (max-width: 413px) {
		.dismissed,
		.scheduled,
		.upcoming {
			max-width: 50px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.table-body-type {
			display: flex;
		}

		.type {
			max-width: 80px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	@media only screen and (max-width: 500px) {
		.table-head-row,
		.table-body-row {
			display: grid;
			gap: 10px;
			flex-direction: row;
			grid-template-columns: 20px repeat(3, 1fr) 20px;
			@include rem('padding', 0px 10px 0px 10px);
			justify-content: space-evenly;
		}

		.table .table-body-row {
			gap: 10px;
		}

		svg {
			padding: 5px;
		}

		.table-head__type,
		.table-head__contact,
		.table-head__status {
			display: flex;
			flex-direction: row;
			max-width: 50px;
		}

		.text-button,
		.table-body__contact,
		.table-body__type,
		.table-body__status {
			max-width: 50px;
			text-overflow: break-word;
		}

		/deep/ .btn-quick-actions {
			justify-content: flex-end;
			@include rem('max-height', 34px);
			@include rem('max-width', 34px);
		}

		.table-head__checkbox,
		.table-body__checkbox,
		.custom-checkbox {
			max-width: 25px;
			padding: 5px;
			justify-content: flex-end;
		}
	}

	.table-head-row {
		@include rem('padding-right', 15px);
	}

	.table-body__type,
	.table-body__contact {
		display: flex;
		flex-direction: row;
	}

	.alert-icon,
	/deep/ .contact-icon {
		@include rem('margin-right', 15px);
	}

	.table-head__quick-actions {
		.quick-actions {
			cursor: default;
		}
	}

	.quick-actions-container {
		display: flex;
		justify-content: flex-end;
	}

	.table-body__quick-actions {
		display: flex;
		justify-content: flex-end;
	}

	.sort-down-icon {
		@include rem('margin-bottom', -4px);
	}

	.sort-up-icon {
		@include rem('margin-bottom', 2px);
	}

	.alert-type,
	.alert-type-info {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	.type {
		color: $brand-blue;
		font-weight: 500;
		cursor: pointer;
	}

	@media only screen and (min-width: 501px) and (max-width: 830px) {
		.table-head-row,
		.table-body-row {
			grid-template-columns: 20px 3fr 4fr 2fr 44px;
		}
	}

	@media only screen and (max-width: 501px) {
		.text-button {
			max-width: 80px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	@media only screen and (max-width: 1200px) {
		.table-head__date,
		.table-body__date,
		.table-head__owner,
		.table-body__owner {
			display: none;
		}

		.table-head-row,
		.table-body-row {
			@include rem('grid-gap', 15px);
		}
	}

	@media only screen and (min-width: 831px) and (max-width: 1200px) {
		.table-head-row,
		.table-body-row {
			grid-template-columns: 20px 3fr 4fr 2fr 44px;
			display: grid;
		}
	}

	@media only screen and (max-width: 720px) {
		/deep/ .contact-icon {
			display: none;
		}
	}

	@media only screen and (max-width: 640px) {
		.alert-icon {
			display: none;
		}
	}
}
</style>